/** @format */

import {
	compareQuestionNumbers,
	getParentQuestionNumber,
	hasDisplayableContent,
	type InheritedPaperFields,
	type ParentGroupSummary,
	type Question,
	type QuestionSeries,
} from "@question-bank/shared";

export interface SelectedPart {
	id: string;
	questionNumber: string;
	maxMarks: number;
}

export interface PaperEntry {
	parentQuestionId: string;
	rootQuestionNumber: string;
	questionNumber: string;
	/** Paper-level facts, resolved from the owning Paper (QB2-223). Optional
	 * because a cart entry can be built before its paper has loaded. */
	series?: QuestionSeries;
	subject?: string | string[];
	totalMarks: number;
	selectedParts?: SelectedPart[];
	totalParts?: number;
}

export const isDescendantOf = (childNumber: string, ancestorNumber: string): boolean => {
	let current: string | null = childNumber;
	while ((current = getParentQuestionNumber(current)) !== null) {
		if (current === ancestorNumber) {
			return true;
		}
	}
	return false;
};

export const selectWithAncestors = (parts: Question[], selected: Set<string>, part: Question): Set<string> => {
	const next = new Set(selected);
	next.add(part.id);
	const byNumber = new Map(parts.map((p) => [p.questionNumber, p]));
	let current: string | null = part.questionNumber;
	while ((current = getParentQuestionNumber(current)) !== null) {
		const ancestor = byNumber.get(current);
		if (ancestor) {
			next.add(ancestor.id);
		}
	}
	return next;
};

export const deselectWithDescendants = (parts: Question[], selected: Set<string>, part: Question): Set<string> => {
	const next = new Set(selected);
	next.delete(part.id);
	for (const candidate of parts) {
		if (isDescendantOf(candidate.questionNumber, part.questionNumber)) {
			next.delete(candidate.id);
		}
	}
	return next;
};

export const togglePart = (parts: Question[], selected: Set<string>, part: Question): Set<string> =>
	selected.has(part.id) ? deselectWithDescendants(parts, selected, part) : selectWithAncestors(parts, selected, part);

export const sumSelectedMarks = (parts: Question[], selected: Set<string>): number =>
	parts.reduce((sum, part) => (selected.has(part.id) ? sum + (part.maxMarks ?? 0) : sum), 0);

export const buildGroupEntry = (group: ParentGroupSummary): PaperEntry => ({
	parentQuestionId: group.parentQuestionId,
	rootQuestionNumber: group.rootQuestionNumber,
	questionNumber: group.questionNumber,
	series: group.series,
	subject: group.subject,
	totalMarks: group.totalMarks ?? 0,
	totalParts: group.childCount,
});

export const buildPartsEntry = (
	root: Question,
	parts: Question[],
	selected: Set<string>,
	/** Paper-level facts resolved from the owning Paper (QB2-223). The root no
	 * longer carries `series`/`subject` itself; the caller supplies them from
	 * the resolved Paper. Omitted when the paper has not loaded. */
	paperFields?: Pick<InheritedPaperFields, "series" | "subject">,
): PaperEntry => {
	const displayableParts = parts.filter(hasDisplayableContent);
	const selectedParts: SelectedPart[] = displayableParts
		.filter((part) => selected.has(part.id))
		.sort((a, b) => compareQuestionNumbers(a.questionNumber, b.questionNumber))
		.map((part) => ({ id: part.id, questionNumber: part.questionNumber, maxMarks: part.maxMarks ?? 0 }));
	return {
		parentQuestionId: root.parentQuestionId,
		rootQuestionNumber: root.rootQuestionNumber,
		questionNumber: root.questionNumber,
		series: paperFields?.series,
		subject: paperFields?.subject,
		totalMarks: selectedParts.reduce((sum, part) => sum + part.maxMarks, 0),
		selectedParts,
		totalParts: displayableParts.length,
	};
};

export interface PartsCount {
	selected: number;
	total: number;
}

export const countEntryParts = (entry: PaperEntry): PartsCount => {
	const total = entry.totalParts ?? entry.selectedParts?.length ?? 0;
	return { selected: entry.selectedParts?.length ?? total, total };
};

export const countParts = (entires: PaperEntry[]): PartsCount =>
	entires.reduce(
		(acc, entry) => {
			const parts = countEntryParts(entry);
			return { selected: acc.selected + parts.selected, total: acc.total + parts.total };
		},
		{ selected: 0, total: 0 },
	);

export const replaceEntry = (entries: PaperEntry[], next: PaperEntry): PaperEntry[] => {
	const index = entries.findIndex((entry) => entry.parentQuestionId === next.parentQuestionId);
	if (index === -1) {
		return [...entries, next];
	}

	return entries.map((entry, i) => (i === index ? next : entry));
};
